import type { TxProgressInfo } from 'src/blockchain';

import { useRef, useState, useEffect, useCallback } from 'react';

import { WALLET_TIMEOUT_MS, WalletTimeoutError } from 'src/utils/walletTimeout';
import { walletErrorMessage, isNoWalletConnected } from 'src/utils/walletErrors';

/**
 * The state behind `TxProgress` — one on-chain action, from the click to the updated deal
 * (contracts v3, UI part §2).
 *
 * Four phases are visible to the buyer and only three of them belong to the wallet. The fourth,
 * `syncing`, is ours: the chain is done, and we are waiting for the server's poller to project the
 * new status (≤2s on the 2-second EVM tick). Ending at `confirmed` would show "done" over a screen
 * still displaying the old status, which is the moment a buyer clicks the same button again.
 *
 * The wallet prompt is bounded, the network wait is not. A wallet popup can hang forever — closed,
 * out of gas, unlocked into another account — so an action that never leaves the `confirm` phase
 * fails on its own. Once the transaction is out, waiting is the correct behaviour however long it
 * takes: the money is already moving, and unblocking the button would only invite a second one.
 */

export type TxProgressPhase = TxProgressInfo['phase'] | 'syncing';

export interface TxProgressState {
  phase: TxProgressPhase;
  txHash?: string;
  step: number;
  of: number;
  /** The step's name from the wallet layer ("Create escrow"); the panel has a default without it. */
  stepLabel?: string;
}

export interface UseTxProgress {
  state: TxProgressState | null;
  error: string | null;
  /**
   * The error is that no wallet is connected (app#315): the screen puts `Connect Wallet` — and
   * with it the connection window — under the message instead of a Retry that would fail again.
   */
  needsWallet: boolean;
  /** True from the click until the deal is updated — the action's button stays disabled. */
  isRunning: boolean;
  /**
   * Run one on-chain action. Resolves `true` when the chain accepted it, `false` when it was
   * refused or failed (the message is in `error`). A screen keeps the panel until the deal's
   * status changes, then calls `reset`; a dialog with its own success screen passes
   * `done` to `TxProgress` instead — it never sees the deal change (app#198).
   */
  run: (
    action: (onProgress: (info: TxProgressInfo) => void, signal: AbortSignal) => Promise<unknown>,
    fallbackMessage: string
  ) => Promise<boolean>;
  reset: () => void;
}

export function useTxProgress(): UseTxProgress {
  const [state, setState] = useState<TxProgressState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [needsWallet, setNeedsWallet] = useState(false);
  const mounted = useRef(true);

  useEffect(
    () => () => {
      mounted.current = false;
    },
    []
  );

  const reset = useCallback(() => {
    setState(null);
    setError(null);
    setNeedsWallet(false);
  }, []);

  // Which run is the live one. A run the prompt timeout gave up on is still executing — the race
  // cannot stop it, and the wallet may yet answer its prompt — and it must not drive this panel any
  // more: its later phases would paint over the next run's, or over the error that ended it.
  const current = useRef(0);

  const run = useCallback<UseTxProgress['run']>(async (action, fallbackMessage) => {
    const id = current.current + 1;

    current.current = id;
    setError(null);
    setNeedsWallet(false);
    setState({ phase: 'confirm', step: 1, of: 1 });

    // Bounds the wallet prompt only: cleared the moment the wallet hands back a transaction. The
    // signal tells the action that nobody is listening any more, so it stops at its next step
    // instead of signing on.
    let left = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const controller = new AbortController();

    const promptTimeout = new Promise<never>((_, reject) => {
      timer = setTimeout(() => {
        if (!left) {
          controller.abort();
          reject(new WalletTimeoutError());
        }
      }, WALLET_TIMEOUT_MS);
    });

    try {
      await Promise.race([
        action((info) => {
          if (info.phase !== 'confirm') {
            left = true;
          }
          if (mounted.current && current.current === id) {
            setState(info);
          }
        }, controller.signal),
        promptTimeout,
      ]);

      if (mounted.current && current.current === id) {
        setState((previous) => ({ ...(previous ?? { step: 1, of: 1 }), phase: 'syncing' }));
      }

      return true;
    } catch (err) {
      if (mounted.current && current.current === id) {
        setError(walletErrorMessage(err, fallbackMessage));
        setNeedsWallet(isNoWalletConnected(err));
        setState(null);
      }

      return false;
    } finally {
      clearTimeout(timer);
    }
  }, []);

  return { state, error, needsWallet, isRunning: state !== null, run, reset };
}
